import { MarketoForm } from '@/features/MarketoForm';
import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import { MuiHidden, MuiTypography } from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { colorMap, Theme } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import { getFallbackImageVideo } from '@/utils/resolvers/resolveVideo';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isEqual } from 'lodash';
import React, { memo, useMemo } from 'react';
import { VideoBackground } from '../Video';
import { CTAProps } from './CTA.interface';
import useStyles from './CTA.styles';

const CTA = ({
  backgroundVideo,
  logo,
  backgroundColor,
  nonThemeBackgroundColor,
  backgroundImage,
  mobileBackgroundImage,
  theme = Theme.light,
  detailContent,
  primaryCTA,
  secondaryCTA,
  className: overrideClassName,
  contentButtons,
  heading,
  superHeading,
  includeMarketo,
  marketoProps,
}: Br.PropsWithContentButtons<CTAProps>) => {
  const ctaTheme = useMemo(
    () =>
      backgroundColor && backgroundColor !== colorMap['no-background-color']
        ? resolveTheme('bg-color')({ rawVal: backgroundColor })
        : theme,
    [backgroundColor, theme],
  );
  const displayBackgroundImage = useMemo(
    () => !nonThemeBackgroundColor && !backgroundColor,
    [nonThemeBackgroundColor, backgroundColor],
  );
  const realTheme = useMemo(
    () => (nonThemeBackgroundColor ? theme : ctaTheme),
    [nonThemeBackgroundColor, theme, ctaTheme],
  );

  const classes = useStyles({});
  const rootClassName = clsx(
    classes.root,
    'cta',
    overrideClassName,
    `${realTheme}-theme`,
    !nonThemeBackgroundColor && `bg-color-${backgroundColor}`,
  );

  const shouldDisplayPrimaryCTA = primaryCTA?.displayText;
  const shouldDisplaySecondaryCTA = secondaryCTA?.displayText;
  const shouldDisplayMarketoForm = useMemo(
    () => includeMarketo && !!marketoProps?.formId,
    [includeMarketo, marketoProps?.formId],
  );
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const bgVideo = {
    ...backgroundVideo,
    fallbackImage: getFallbackImageVideo({
      isMobile,
      mobileBackgroundImage,
      fallbackImage: backgroundVideo?.fallbackImage,
    }),
  };
  const shouldDisplayVideoOrImage = useMemo(
    () => bgVideo?.videoUrl && displayBackgroundImage,
    [bgVideo?.videoUrl, displayBackgroundImage],
  );

  return (
    <section
      data-testid="qa-cta-component"
      className={clsx(rootClassName, 'gntc-cta')}
      style={{
        background: nonThemeBackgroundColor,
      }}
    >
      {contentButtons?.content}
      {displayBackgroundImage && (
        <>
          {(backgroundImage || mobileBackgroundImage) && (
            <div className={classes.bgImage}>
              {backgroundImage && (
                <MuiHidden smDown>
                  <Image image={backgroundImage} type="background" size="cover" />
                </MuiHidden>
              )}
              {mobileBackgroundImage && (
                <MuiHidden smUp>
                  <Image image={mobileBackgroundImage} type="background" size="cover" />
                </MuiHidden>
              )}
            </div>
          )}
        </>
      )}
      {shouldDisplayVideoOrImage && <VideoBackground {...bgVideo} />}
      <div className={classes.columnContent}>
        {superHeading && (
          <MuiTypography
            data-testid="qa-cta-component-superHeading"
            variant="overline"
            component="h5"
            className={clsx(classes.heading, !heading && classes.noMargin)}
          >
            {superHeading}
          </MuiTypography>
        )}
        {heading && (
          <MuiTypography
            data-testid="qa-cta-component-heading"
            variant="h5"
            component="h4"
            className={clsx(classes.heading, !superHeading && classes.noMargin, 'gtm-cta-headline')}
          >
            {heading}
          </MuiTypography>
        )}
        {logo && (
          <div
            className={clsx(classes.img, detailContent && 'margin-bottom')}
            data-testid="qa-cta-component-logo"
          >
            <Image image={logo} base="original" size="contain" position="left" />
          </div>
        )}

        {detailContent && (
          <CaptureClicksRichText
            customStyles
            data-testid="qa-cta-component-text"
            content={detailContent}
            className={clsx('cta-rich-text', classes.rtc, 'gtm-cta')}
            theme={realTheme}
          />
        )}
      </div>
      {(shouldDisplayPrimaryCTA || shouldDisplaySecondaryCTA || shouldDisplayMarketoForm) && (
        <div
          data-testid="qa-cta-component-buttons"
          className={clsx('cta-buttons', classes.columnCta)}
        >
          {shouldDisplayPrimaryCTA && (
            <Button
              data-testid="qa-cta-component-primary-button"
              theme={realTheme}
              link={primaryCTA}
              className="gtm-cta"
            />
          )}
          {shouldDisplaySecondaryCTA && (
            <Button
              theme={realTheme}
              data-testid="qa-cta-component-secondary-button"
              outlined
              link={secondaryCTA}
              className={clsx(classes.secondaryCta, 'gtm-cta')}
            />
          )}
          {shouldDisplayMarketoForm && (
            <div className={classes.marketoCta}>
              <MarketoForm {...marketoProps} />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default memo(CTA, isEqual);
